import type { Alignment } from "@platejs/basic-styles";
import { TextAlignPlugin } from "@platejs/basic-styles/react";
import AlignCenterIcon from "@redotech/redo-icons/align-center.svg";
import AlignJustifyIcon from "@redotech/redo-icons/align-justify.svg";
import AlignLeftIcon from "@redotech/redo-icons/align-left.svg";
import AlignRightIcon from "@redotech/redo-icons/align-right.svg";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import { useEditorPlugin, useSelectionFragmentProp } from "platejs/react";
import { memo, useMemo, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import {
  RedoCommandMenu,
  type RedoCommandMenuItem,
} from "../../arbiter-components/command-menu/redo-command-menu";
import { RedoIcon } from "../../arbiter-components/icon/redo-icon";
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "../ui/dropdown-menu";
import { useToolbarOverflow } from "./toolbar-overflow";

const ALIGN_ITEMS = [
  { Icon: AlignLeftIcon, value: "left", text: "Align left" },
  { Icon: AlignCenterIcon, value: "center", text: "Align center" },
  { Icon: AlignRightIcon, value: "right", text: "Align right" },
  { Icon: AlignJustifyIcon, value: "justify", text: "Justify" },
] as const;

export const AlignToolbarButton = memo(function AlignToolbarButton() {
  const { editor, tf } = useEditorPlugin(TextAlignPlugin);
  const value =
    useSelectionFragmentProp({
      defaultValue: "start",
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      getProp: (node) => node.align as string,
    }) ?? "left";

  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);

  const IconValue =
    ALIGN_ITEMS.find((item) => item.value === value)?.Icon ?? AlignLeftIcon;

  const items: RedoCommandMenuItem[] = useMemo(
    () =>
      ALIGN_ITEMS.map(({ Icon, value: itemValue, text }) => ({
        Icon,
        text,
        onClick: () => {
          tf.textAlign.setNodes(itemValue as Alignment);
          editor.tf.focus();
        },
      })),
    [editor, tf],
  );

  const { isOverflow } = useToolbarOverflow();

  if (isOverflow) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <RedoIcon Icon={AlignLeftIcon} size={16} />
          Align text
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {ALIGN_ITEMS.map(({ Icon, value: itemValue, text }) => (
            <DropdownMenuItem
              key={itemValue}
              onSelect={() => {
                tf.textAlign.setNodes(itemValue as Alignment);
                editor.tf.focus();
              }}
            >
              <RedoIcon Icon={Icon} size={16} />
              {text}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <>
      <RedoButton
        hierarchy="tertiary"
        IconLeading={IconValue}
        IconTrailing={ChevronDownIcon}
        onClick={() => setOpen((prev) => !prev)}
        ref={setAnchor}
        size="sm"
        text=""
      />
      <RedoCommandMenu
        anchor={anchor}
        items={items}
        open={open}
        placement="bottom-start"
        setOpen={setOpen}
      />
    </>
  );
});
